import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Alert,
  LinearProgress,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AccountBalance as PlanIcon,
  Group as MembersIcon,
  Folder as ProjectsIcon,
  Storage as StorageIcon,
  AttachMoney as PriceIcon,
  CheckCircle as ActiveIcon,
  Cancel as CancelledIcon
} from '@mui/icons-material';
import { colors } from '../../utils/colors';
import { getCustomerDetailsAPI } from '../../lib/api';

const CustomerSubscription = ({ customerId }) => {
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (customerId) {
      fetchSubscriptionData();
    }
  }, [customerId]);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await getCustomerDetailsAPI(customerId);

      if (result.status && result.data) {
        setSubscriptionData(result.data);
      } else {
        setError('Failed to load subscription data');
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setError(error.message || 'Failed to fetch subscription details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatStorage = (bytes) => {
    if (!bytes) return '0 MB';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(1)} GB`;
    }
    return `${Math.round(mb)} MB`;
  };

  // Helper function to get subscription type display
  const getSubscriptionType = (subscription) => {
    // Check if subscription has a type property
    if (subscription.type) {
      return subscription.type === 'free' ? 'Trial' : subscription.type;
    }
    // Check if plan has a type property
    if (subscription.plan?.type) {
      return subscription.plan.type === 'free' ? 'Trial' : subscription.plan.type;
    }
    // Default to empty string
    return '';
  };

  // Helper function to format plan name with type
  const formatPlanWithType = (subscription) => {
    const planName = subscription.plan?.name || 'N/A';
    const type = getSubscriptionType(subscription);
    
    // Only show type if it's "Trial" and plan name is "Basic"
    // Using toLowerCase() for case-insensitive comparison
    if (type.toLowerCase() === 'trial' && planName.toLowerCase() === 'basic') {
      return `${planName} / ${type}`;
    }
    
    // For all other cases, just show the plan name
    return planName;
  };

  // Helper function to get monthly price based on subscription type
  const getMonthlyPrice = (subscription) => {
    const type = getSubscriptionType(subscription);
    const isFreeOrTrial = type.toLowerCase() === 'trial' || type.toLowerCase() === 'free';
    
    if (isFreeOrTrial) {
      return '0.0';
    }
    
    // Return the actual price from plan if available
    return subscription.plan?.price_per_month || '0.0';
  };

  const StatCard = ({ icon: Icon, label, value, color = colors.primary.main }) => (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        background: theme.palette.background.paper,
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 36, sm: 40, md: 44 },
              height: { xs: 36, sm: 40, md: 44 },
              borderRadius: 1.5,
              bgcolor: `${color}08`,
              border: `1px solid ${color}20`,
              mr: 2,
              flexShrink: 0
            }}
          >
            <Icon 
              sx={{ 
                fontSize: { xs: 18, sm: 20, md: 22 }, 
                color 
              }} 
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 500,
                display: 'block',
                mb: 0.5
              }}
            >
              {label}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '0.95rem', sm: '1.05rem', md: '1.15rem' },
                fontWeight: 600,
                color: theme.palette.text.primary,
                lineHeight: 1.3
              }}
            >
              {value}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
       <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', zIndex:2001 }}>
        <LinearProgress color="primary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ borderRadius: 1.5 }}>
          <Typography variant="body2" fontWeight={500}>
            {error}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (!subscriptionData || !subscriptionData.subscription) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
          No active subscription found
        </Typography>
      </Box>
    );
  }

  const { subscription } = subscriptionData;
  const { plan } = subscription;
  const isActive = subscription.subscription_cancelled_at === null;
  
  // Get the monthly price based on subscription type
  const monthlyPrice = getMonthlyPrice(subscription);
  const subscriptionType = getSubscriptionType(subscription);
  const isFreeOrTrial = subscriptionType.toLowerCase() === 'trial' || subscriptionType.toLowerCase() === 'free';

  return (
    <Box sx={{ width: '100%', p: { xs: 2, sm: 3 } }}>

      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            mb: 3
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1.35rem', sm: '1.5rem', md: '1.65rem' },
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 0.5
              }}
            >
              Subscription Details
            </Typography>
          </Box>
          
          <Chip
            icon={isActive ? <ActiveIcon /> : <CancelledIcon />}
            label={isActive ? 'Active' : 'Cancelled'}
            sx={{
              bgcolor: isActive 
                ? colors.success?.main || '#4caf50' 
                : colors.error?.main || '#f44336',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.8rem',
              px: 2,
              height: 32,
              '& .MuiChip-icon': {
                color: 'white !important',
                fontSize: 18
              }
            }}
          />
        </Box>

        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            background: `linear-gradient(135deg, ${colors.primary.main}08 0%, ${colors.primary.light}08 100%)`,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 3.5 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="overline"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    letterSpacing: '0.5px',
                    display: 'block',
                    mb: 1
                  }}
                >
                  Current Plan
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                      color: theme.palette.text.primary
                    }}
                  >
                    {formatPlanWithType(subscription)}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px',
                      display: 'block',
                      mb: 1
                    }}
                  >
                    Monthly Price
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                      color: theme.palette.text.primary,
                      opacity: isFreeOrTrial ? 0.7 : 1
                    }}
                  >
                    ${monthlyPrice}
                    {!isFreeOrTrial && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                          color: 'text.secondary',
                          ml: 0.5,
                          fontWeight: 400
                        }}
                      >
                        /month
                      </Typography>
                    )}
                  </Typography>
                  {isFreeOrTrial && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        color: colors.success?.main || '#4caf50',
                        fontWeight: 600,
                        fontSize: '0.75rem'
                      }}
                    >
                      Free Plan
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <StatCard
              icon={CalendarIcon}
              label="Start Date"
              value={formatDate(subscription.start_date)}
              color={colors.success?.main || '#4caf50'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StatCard
              icon={CalendarIcon}
              label="End Date"
              value={formatDate(subscription.end_date)}
              color={colors.warning?.main || '#ff9800'}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
             <StatCard
              icon={PriceIcon}
              label="Subscription ID"
              value={`#${subscription.id}`}
              color='#4caf50'
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={MembersIcon}
              label="Members Per Project"
              value={plan.number_of_members_per_project}
              color='#9c27b0'
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={StorageIcon}
              label="Storage Limit"
              value={`${plan.storage} MB`}
              color='#ff5722'
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={ProjectsIcon}
              label="Projects"
              value={plan.number_of_projects}
              color={colors.primary.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              icon={StorageIcon}
              label="Storage Used"
              value={formatStorage(subscriptionData.total_storage)}
              color='#00bcd4'
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
              <StatCard
              icon={PlanIcon}
              label="Plan ID"
              value={`#${plan.id}`}
              color='#607d8b'
            />
          </Grid>
        </Grid>
      </Box>

      {subscription.subscription_cancelled_at && (
        <Alert severity="warning" sx={{ mt: 3, borderRadius: 1.5 }}>
          <Typography variant="body2" fontWeight={500}>
            Subscription was cancelled on {formatDate(subscription.subscription_cancelled_at)}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default CustomerSubscription;